import { useEffect, useState } from 'react'
import { supabase } from '../../api/supabase'
import { useToast } from '../../hooks/useToast'

const JOB_REF_FORMAT = /^(KI|KE)[0-9]{5}$/

interface PreReserveDialogProps {
  onClose: () => void
  onReserved?: () => void
}

// Z31: claim a job ref ahead of actually creating the job (e.g. quoting a customer a KI
// number before the paperwork's in) -- generate_job_ref()/preview_next_job_ref skip active
// reservations in their MAX calc, so AutoJobs/manual creation won't hand this number to
// someone else in the meantime. See 20260903e_z31_job_ref_reservations.sql.
export default function PreReserveDialog({ onClose, onReserved }: PreReserveDialogProps) {
  const { toast } = useToast()

  const [direction, setDirection] = useState<'IMP' | 'EXP'>('IMP')
  const [refValue, setRefValue] = useState('')
  const [note, setNote] = useState('')
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoadingPreview(true)
    setError(null)
    supabase.rpc('preview_next_job_ref', { p_direction: direction }).then(({ data, error: rpcError }) => {
      if (cancelled) return
      if (rpcError) {
        setError(rpcError.message)
      } else if (data) {
        setRefValue(data)
      }
      setLoadingPreview(false)
    })
    return () => { cancelled = true }
  }, [direction])

  const formatValid = JOB_REF_FORMAT.test(refValue)

  async function handleSave() {
    if (!formatValid) {
      setError('Reference must match KI/KE + 5 digits, e.g. KI26099')
      return
    }
    setSaving(true)
    setError(null)
    try {
      // Z36 redo: reserving goes through reserve_job_ref() -- a released (cancelled) number is
      // reactivated instead of failing on the unique key, and the server says exactly why a number is not available.
      const { error: rpcErr } = await supabase.rpc('reserve_job_ref', {
        p_direction: direction, p_ref: refValue, p_note: note.trim() || null,
      })
      if (rpcErr) throw new Error(rpcErr.message)
      toast(`Reserved ${refValue}`, 'success')
      onReserved?.()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reserve job reference')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Pre-reserve Job Reference</h2>
          <button onClick={onClose} disabled={saving} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Direction</span>
            <div className="mt-1 flex gap-2">
              {(['IMP', 'EXP'] as const).map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDirection(d)}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-md border font-medium transition-colors ${
                    direction === d
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {d === 'IMP' ? 'Import (KI)' : 'Export (KE)'}
                </button>
              ))}
            </div>
          </label>

          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Job Reference</span>
            <input
              type="text"
              value={loadingPreview ? '' : refValue}
              onChange={e => setRefValue(e.target.value.toUpperCase())}
              disabled={loadingPreview}
              placeholder={loadingPreview ? 'Loading suggestion…' : 'KI/KE + 5 digits'}
              className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-mono font-bold dark:bg-gray-700 dark:text-white disabled:opacity-50 ${
                refValue && !formatValid ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {refValue && !formatValid && (
              <p className="text-xs text-red-500 mt-1">Must match KI/KE + 5 digits</p>
            )}
          </label>

          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Note</span>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              placeholder="Optional — why this ref is being held"
              className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white"
            />
          </label>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loadingPreview || !formatValid}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
